import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Store, Plus, Filter } from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { ShopCard, SkeletonList, Empty, SectionHead, Avatar, TierBadge } from '../components/ui';

const KINDS = [['', 'All'], ['local', '🌱 Local'], ['national', '🏭 National'], ['online_shop', '🛒 Online']];

export default function Shops() {
  const [sp] = useSearchParams();
  const { city, user, shops: myShops } = useAuth();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [cityOnly, setCityOnly] = useState(!!city);

  const { data, isLoading } = useQuery({
    queryKey: ['shops', q, kind, cityOnly, city?.handle],
    queryFn: () => api.get('/shops', {
      params: { q: q || undefined, kind: kind || undefined, city: cityOnly && city ? city.handle : undefined, limit: 40, sort: 'community' },
    }).then(r => r.data),
  });

  const { data: loyalty } = useQuery({ queryKey: ['myLoyalty'], queryFn: () => api.get('/purchases/my-loyalty').then(r => r.data) });

  if (sp.get('mine') === '1') return <MyShops shops={myShops} />;

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card p-5 bg-gradient-to-br from-mint-500 to-emerald-600 text-white">
        <h1 className="font-display text-2xl font-extrabold">Shops & producers</h1>
        <p className="text-white/85 text-sm mt-1">
          Every purchase = points. Buy often at the same shop and your multiplier grows to <b>2×</b>.
        </p>
        {user?.role !== 'producer' && (
          <Link to="/create-shop" className="btn bg-white text-emerald-700 btn-sm mt-3 font-extrabold">
            <Plus size={14} /> Open my shop
          </Link>)}
      </div>

      {loyalty?.length > 0 && (
        <div>
          <SectionHead title="Your loyalty" sub="Your multiplier at each shop" />
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {loyalty.slice(0, 8).map(l => (
              <Link key={l.shop_id} to={`/shop/${l.shop_slug}`}
                className="card-h p-3 min-w-[150px] shrink-0">
                <Avatar src={l.logo_url} name={l.shop_name} size={36} square />
                <p className="font-bold text-xs mt-2 truncate">{l.shop_name}</p>
                <TierBadge tier={l.tier} label={l.tier_label} multiplier={l.multiplier} />
                {l.next_tier && (
                  <p className="text-[10px] text-ink-400 font-semibold mt-1">
                    {Math.round(l.next_tier.spent_needed)} more to T{l.next_tier.tier}
                  </p>)}
              </Link>))}
          </div>
        </div>
      )}

      <div className="space-y-2 sticky top-14 z-20 py-2 -mx-3 px-3 bg-ink-50/90 backdrop-blur">
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-10" placeholder="Search shops…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
          {KINDS.map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition
                ${kind === k ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>{l}</button>))}
          {city && (
            <button onClick={() => setCityOnly(v => !v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition
                ${cityOnly ? 'bg-brand-500 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>
              {city.handle}
            </button>)}
        </div>
      </div>

      {isLoading ? <SkeletonList n={6} h="h-20" />
        : !data?.length ? <Empty icon={Store} title="No shops found" sub="Try a different filter or city." />
        : <div className="space-y-2">{data.map(s => <ShopCard key={s.id} shop={s} />)}</div>}
    </div>
  );
}

function MyShops({ shops }) {
  return (
    <div className="space-y-3 animate-fade-up">
      <SectionHead title="My shops" right={<Link to="/create-shop" className="btn-primary btn-sm"><Plus size={13} /> New</Link>} />
      {!shops?.length ? <Empty icon={Store} title="No shops yet" action={<Link to="/create-shop" className="btn-primary btn-sm">Open a shop</Link>} />
        : shops.map(s => (
          <Link key={s.id} to={s.status === 'approved' ? `/shop/${s.id}/dashboard` : `/shop/${s.slug}`}
            className="card-h p-4 flex items-center gap-3">
            <Avatar src={s.logo_url} name={s.name} size={48} square />
            <div className="flex-1">
              <p className="font-bold">{s.name}</p>
              <span className={`chip ${s.status === 'approved' ? 'chip-mint' : s.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                {s.status.toUpperCase()}
              </span>
            </div>
            {s.status === 'approved' && <span className="btn-primary btn-sm">Dashboard</span>}
          </Link>))}
    </div>
  );
}